/**
 * C.2/C.5 — React hook over `api.portalFeatureFlags.getForPortal`.
 *
 * Returns the four Milestone-C flags for the current portal. Demo mode
 * reads from `demoPortalFeatureFlags` keyed by `showcaseSessionId`; live
 * mode reads `portalFeatureFlags` keyed by `clientSlug`. Defaults all
 * off for live, all on for demo (matching Q20).
 */

import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useShowcaseSessionId } from '../showcase/showcaseSession';
import { useShowcaseVisible } from '../showcase/useShowcaseVisible';
import { useOptionalTenantBranding } from './TenantBrandingContext';

export interface PortalFeatureFlags {
  obsidianVault: boolean;
  nipeEnabled: boolean;
  canvasFlow: boolean;
  dataviewBases: boolean;
}

const DEFAULT_FLAGS: PortalFeatureFlags = {
  obsidianVault: false,
  nipeEnabled: false,
  canvasFlow: false,
  dataviewBases: false,
};

// Demo mode default — all flags ON so anonymous demo users see every
// Milestone-C surface. Matches the Convex-side DEMO_DEFAULT_FLAGS in
// `convex/portalFeatureFlags.ts`.
const DEMO_DEFAULT_FLAGS: PortalFeatureFlags = {
  obsidianVault: true,
  nipeEnabled: true,
  canvasFlow: true,
  dataviewBases: true,
};

export function useFeatureFlags(): PortalFeatureFlags {
  // Soft read — the Win95 desktop also renders the global root route
  // outside `/c/${slug}`, so we must not throw when there's no branding
  // provider above us.
  const branding = useOptionalTenantBranding();
  const [isDemo] = useShowcaseVisible();
  const showcaseSessionId = useShowcaseSessionId();
  const flags = useQuery(
    api.portalFeatureFlags.getForPortal,
    branding
      ? {
          clientSlug: branding.slug,
          ...(isDemo ? { demoSessionId: showcaseSessionId } : {}),
        }
      : 'skip',
  );
  // Root demo desktop (no portal context, but demo flag on): use the
  // demo defaults so Obsidian Vault / Import / Graph icons appear.
  // Without a portal AND not in demo mode: live default-off.
  if (!branding) {
    return isDemo ? DEMO_DEFAULT_FLAGS : DEFAULT_FLAGS;
  }
  return flags ?? (isDemo ? DEMO_DEFAULT_FLAGS : DEFAULT_FLAGS);
}
